import { useEffect, useMemo, useState } from "react";
import { dateGroupKey } from "../components/date-ledger";

type Doc = { id: number; created_at: string };

/**
 * Tovar harakatlari jurnalidan kelgan hujjatga olib boradi.
 *
 * Nega hujjat ID bo'yicha ALOHIDA so'raladi: ledger sahifalari 200 talik
 * bo'laklarda yuklaydi, ya'ni yarim yil oldingi hujjatga yetish uchun o'nlab
 * sahifa "yuklab borish" kerak bo'lardi. Bitta hujjatni olib ro'yxatga
 * qo'shib qo'yish — bir so'rov, va kunlar ustuni ham to'liq qolaveradi
 * (kun bo'yicha filtrlash uni qisqartirib yuborardi).
 *
 * Hujjat ro'yxat holatiga YOZILMAYDI, alohida saqlanadi va har renderda
 * qo'shib beriladi. Sabab: ro'yxat qayta yuklanishi mumkin (filial almashdi,
 * hujjat tahrirlandi) va `setItems(pg.items)` qo'shilgan qatorni yuvib
 * yuborardi; "ko'proq yuklash" esa o'sha hujjatni ikkinchi marta olib kelib,
 * React'da takroriy `key` hosil qilardi. Render paytida birlashtirish ikkala
 * holatni ham o'zidan-o'zi hal qiladi.
 */
export function useFocusDoc<T extends Doc>({
  focusDoc,
  items,
  fetchOne,
  onSelectDate,
}: {
  /**
   * `seq` — bir xil hujjat qayta so'ralsa ham effekt qayta ishga tushsin.
   * Faqat `id` bo'lganda ikkinchi bosish umuman sezilmasdi.
   */
  focusDoc?: { id: number; seq: number } | null;
  items: T[];
  fetchOne: (id: number) => Promise<T>;
  onSelectDate: (key: string) => void;
}): { items: T[]; highlightId: number | null } {
  const [extra, setExtra] = useState<T | null>(null);
  const seq = focusDoc?.seq;
  const focusDocId = focusDoc?.id;

  useEffect(() => {
    if (focusDocId == null) return;
    let alive = true;
    (async () => {
      let doc: T;
      try {
        doc = await fetchOne(focusDocId);
      } catch {
        // Hujjat o'chirilgan yoki yetib bo'lmaydi — jim qolamiz, foydalanuvchi
        // shunchaki odatdagi ro'yxatni ko'radi.
        return;
      }
      if (!alive) return;
      setExtra(doc);
      onSelectDate(dateGroupKey(doc.created_at));
    })();
    return () => {
      alive = false;
    };
    // Faqat `focusDocId`/`seq` ga bog'lanamiz: qolgan argumentlar har renderda
    // yangi funksiya bo'lib keladi va effekt cheksiz qayta ishga tushardi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusDocId, seq]);

  const merged = useMemo(
    () =>
      extra && !items.some((x) => x.id === extra.id) ? [...items, extra] : items,
    [items, extra],
  );

  return { items: merged, highlightId: extra?.id ?? null };
}
